import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { Dropdown, Select } from "antd";
import { Braces, FileStack, PenLine, Table2 } from "lucide-react";

import { BANNER_MAX_CM, PAGE_EDITOR_CSS, bannerHeightCm, cmToPx, computePageBreaks, pageAt } from "./pageGeometry";
import { COL_WIDTH_CSS, attachColumnResize, clearColumnWidths } from "./tableColumns";

/*
 * Text size in points, for MOU documents. Stored as a class (ql-fs-14 = 14pt)
 * rather than an inline style: the server's HTML sanitizer drops every style
 * attribute but keeps ql-* classes, and mou/pdf.py maps each class to the same
 * point size, so the PDF matches the editor. Its own "fontsize" format, not
 * Quill's "size", so the email editor's small/large/huge keep working.
 * Keep FONT_SIZES in step with FONT_SIZES_PT in trustedge mou/pdf.py.
 */
export const FONT_SIZES = ["8", "9", "10", "11", "12", "14", "16", "18", "20", "24", "28", "32", "36", "48"];
const Parchment = Quill.import("parchment");
const Delta = Quill.import("delta");
const FontSize = new Parchment.ClassAttributor("fontsize", "ql-fs", {
  scope: Parchment.Scope.INLINE,
  whitelist: FONT_SIZES,
});
Quill.register({ "formats/fontsize": FontSize }, true);

const FONT_SIZE_CSS = `
.ql-snow .ql-picker.ql-fontsize { width: 64px; }
.ql-snow .ql-picker.ql-fontsize .ql-picker-label::before { content: "Size"; }
.ql-snow .ql-picker.ql-fontsize .ql-picker-label[data-value]::before { content: attr(data-value) "pt"; }
.ql-snow .ql-picker.ql-fontsize .ql-picker-item::before { content: attr(data-value); }
${FONT_SIZES.map((s) => `.ql-editor .ql-fs-${s} { font-size: ${s}pt; }`).join("\n")}
`;

/**
 * Rich-text editor for MOU documents, templates and emails, with an
 * "Insert field" picker that drops a {{merge_field}} at the cursor.
 *
 * No image button by default, for the reason RichTextEditor gives: Quill
 * embeds a picked file as a base64 data URI, which would bloat every MOU body
 * and the PDF. `images` turns it on for the header/footer, where a logo is
 * the point; the server re-encodes and caps it. Output is HTML, sanitised
 * server-side on every write.
 *
 * `fields` is the [{key, label}] list from GET /mou/templates/. Leave it empty
 * for a document that has already been rendered (nothing left to merge).
 * `layoutFields` adds the Page break / Signature block buttons, `tables` the
 * table tools, and `pageGuides` ({ customHeader, customFooter }) lays the
 * text out at the PDF's width and marks where each page ends.
 */

const TABLE_ACTIONS = [
  { key: "insert",      label: "Insert table (3 × 3)" },
  { key: "rowBelow",    label: "Add row below" },
  { key: "rowAbove",    label: "Add row above" },
  { key: "colRight",    label: "Add column right" },
  { key: "colLeft",     label: "Add column left" },
  { type: "divider" },
  { key: "resetWidths", label: "Reset column widths" },
  { type: "divider" },
  { key: "deleteRow",   label: "Delete row", danger: true },
  { key: "deleteCol",   label: "Delete column", danger: true },
  { key: "deleteTable", label: "Delete table", danger: true },
];

/**
 * Quill's own insertTable goes wherever Quill last saved a cursor - the top of
 * the document if you never clicked in it - and one put in or next to another
 * table merges into it. Here the table goes at `at` (the last place the user
 * clicked; the end of the document otherwise) on lines of its own: after the
 * table the cursor is in, with a blank line between it and any table beside
 * it, and the text after it stays a paragraph to keep typing in. The cursor
 * lands in the first cell, scrolled into view.
 */
export function insertTable(q, t, rows, cols, at) {
  const end = Math.max(0, q.getLength() - 1);
  let index = Math.min(at ? at.index : end, end);
  const [table] = t.getTable({ index, length: 0 });
  if (table) index = q.getIndex(table) + table.length();
  const inTable = (i) => i >= 0 && i < q.getLength() && !!q.getFormat(i, 0).table;
  const [, offset] = q.getLine(index);
  // Text before the cursor, or a table just above, stays above the new one.
  if (offset > 0 || inTable(index - 1)) {
    q.insertText(index, "\n", { table: false }, "user");
    index += 1;
  }
  // A table straight after keeps a blank line between the two.
  if (inTable(index)) q.insertText(index, "\n", { table: false }, "user");
  let delta = new Delta().retain(index);
  for (let r = 0; r < rows; r += 1) {
    const row = `row-${Math.random().toString(36).slice(2, 6)}`;
    delta = delta.insert("\n".repeat(cols), { table: row });
  }
  q.updateContents(delta, "user");
  q.setSelection(index, 0, "user");
  q.scrollSelectionIntoView?.();
}

// antd opens menus on <body> at z-index ~1050, which is behind the editor's
// full-screen layer (EditorWorkspace, z-[1500]) — there they open inside it.
const popupContainer = (trigger) => trigger.closest("[data-testid=editor-fullscreen]") || document.body;

export default function MergeFieldEditor({ value, onChange, fields = [], height = 320,
                                           placeholder, readOnly = false, layoutFields = [],
                                           tables = false, images = false, pageGuides = null,
                                           compact = false, semantic = false, sizes, onCursorPage }) {
  const quillRef = useRef(null);
  const wrapRef  = useRef(null);
  const [guides, setGuides] = useState({ breaks: [], pages: 1, offset: 0, scroll: 0 });
  const [cursorPage, setCursorPage] = useState(1);
  // Tell the workspace which page the cursor is on, for the live preview.
  useEffect(() => { if (onCursorPage) onCursorPage(cursorPage); }, [cursorPage, onCursorPage]);
  // Size picker on documents (the editors with tables or images) — the PDF
  // honours it. Emails do not: mail clients ignore the class.
  const withSizes = sizes ?? (!semantic && (tables || images));

  const modules = useMemo(() => ({
    toolbar: readOnly ? false : (compact ? [
      withSizes ? [{ fontsize: FONT_SIZES }] : [],
      ["bold", "italic", "underline"],
      [{ align: [] }],
      images ? ["image", "link"] : ["link"],
      ["clean"],
    ].filter((g) => g.length) : [
      [{ header: [1, 2, 3, false] }],
      ...(withSizes ? [[{ fontsize: FONT_SIZES }]] : []),
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
      [{ align: [] }],
      images ? ["blockquote", "link", "image"] : ["blockquote", "link"],
      ["clean"],
    ]),
    // Only when wanted: Quill instantiates every key present, even `false`.
    ...(tables ? { table: true } : {}),
    clipboard: { matchVisual: false },
  }), [readOnly, tables, images, compact, withSizes]);

  const editor = () => quillRef.current?.getEditor?.();

  const insert = (key) => {
    const q = editor();
    if (!q || !key) return;
    const range = q.getSelection(true) || { index: q.getLength() };
    const text = `{{${key}}}`;
    q.insertText(range.index, text, "user");
    q.setSelection(range.index + text.length, 0, "user");
  };

  // Page break and signature block act on whole lines — the PDF swaps the
  // line for a page break or a full-width signature table — so they need a
  // line of their own. Split only where there is text on that side of the
  // cursor; on an empty line it goes in as is, with no stray blank line.
  const insertBlock = (key) => {
    const q = editor();
    if (!q) return;
    const range = q.getSelection(true) || { index: q.getLength() - 1 };
    const [line, offset] = q.getLine(range.index);
    const after = line ? line.length() - 1 - offset : 0;
    const lead = offset > 0 ? "\n" : "";
    const text = `${lead}{{${key}}}${after > 0 ? "\n" : ""}`;
    q.insertText(range.index, text, "user");
    q.setSelection(range.index + lead.length + key.length + 4, 0, "user");
  };

  // Where the user last put the cursor. Clicking the Table menu takes focus
  // away, and Quill alone would fall back to the top of the document.
  const lastSel = useRef(null);
  const onSelection = useCallback((range, source) => { if (range && source === "user") lastSel.current = range; }, []);

  const tableAction = ({ key }) => {
    const q = editor();
    const t = q?.getModule("table");
    if (!t) return;
    q.focus();
    const run = {
      insert: () => insertTable(q, t, 3, 3, lastSel.current),
      rowBelow: () => t.insertRowBelow(),
      rowAbove: () => t.insertRowAbove(),
      colRight: () => t.insertColumnRight(),
      colLeft: () => t.insertColumnLeft(),
      deleteRow: () => t.deleteRow(),
      deleteCol: () => t.deleteColumn(),
      deleteTable: () => t.deleteTable(),
      resetWidths: () => clearColumnWidths(q),
    }[key];
    run?.();
  };

  // ── page guides ────────────────────────────────────────────────────────
  // The header and footer as the PDF prints them: a one-picture letterhead
  // spans the text width and pushes the body down (pdf.py _banner), so its
  // height changes where every page ends.
  const [bands, setBands] = useState({ headerCm: null, footerCm: null });
  const headerHtml = pageGuides?.header || "";
  const footerHtml = pageGuides?.footer || "";
  useEffect(() => {
    if (!pageGuides) return undefined;
    let live = true;
    Promise.all([bannerHeightCm(headerHtml), bannerHeightCm(footerHtml)])
      .then(([headerCm, footerCm]) => { if (live) setBands({ headerCm, footerCm }); });
    return () => { live = false; };
  }, [headerHtml, footerHtml, !!pageGuides]);   // eslint-disable-line react-hooks/exhaustive-deps

  const geometryKey = pageGuides
    ? `${!!pageGuides.customHeader}-${!!pageGuides.customFooter}-${bands.headerCm}-${bands.footerCm}` : "";

  // Slots between Quill's toolbar and text, and after the text, for the
  // header and footer previews - so they read as the top and bottom of the
  // page rather than as part of the toolbar.
  // Checked after every render, not once: ReactQuill builds its editor after
  // its own mount and rebuilds the whole block when it regenerates, which
  // throws away anything inserted into it. Until the editor exists, retry on
  // the next frame.
  const [slots, setSlots] = useState(null);
  const slotsRef = useRef(null);
  // No dependency list on purpose (see above); it returns early once the
  // slots sit in the current editor, so it cannot loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const q = editor();
    const container = q?.container;
    const parent = container?.parentNode;
    const current = slotsRef.current;
    if (!pageGuides) {
      if (current) { current.top.remove(); current.bottom.remove(); slotsRef.current = null; setSlots(null); }
      return undefined;
    }
    if (!parent) {
      const frame = requestAnimationFrame(() => setSlots((s) => (s ? { ...s } : s === null ? undefined : null)));
      return () => cancelAnimationFrame(frame);
    }
    if (current && current.top.parentNode === parent && current.top.nextSibling === container) return undefined;
    if (current) { current.top.remove(); current.bottom.remove(); }
    const top = document.createElement("div");
    const bottom = document.createElement("div");
    parent.insertBefore(top, container);
    parent.insertBefore(bottom, container.nextSibling);
    slotsRef.current = { top, bottom };
    setSlots({ top, bottom });
    return undefined;
  });
  useEffect(() => () => {
    const s = slotsRef.current;
    if (s) { s.top.remove(); s.bottom.remove(); }
    slotsRef.current = null;
  }, []);

  // Drag a table column's border to resize it (tableColumns.js). Attached to
  // whichever Quill instance is current, for the same reason as the slots.
  const resizeRef = useRef({ quill: null, detach: null });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const q = editor();
    const cur = resizeRef.current;
    const want = tables && !readOnly ? q : null;
    if (cur.quill === want) return;
    if (cur.detach) cur.detach();
    resizeRef.current = { quill: want, detach: want && wrapRef.current ? attachColumnResize(want, wrapRef.current) : null };
  });
  useEffect(() => () => { resizeRef.current.detach?.(); }, []);

  const measure = useCallback(() => {
    const q = editor();
    if (!q || !pageGuides || !wrapRef.current) return;
    const root = q.root;
    const { breaks, pages } = computePageBreaks(root, { ...pageGuides, ...bands });
    // Children's offsetTop is relative to .ql-container; place the overlay
    // from the container's position inside our wrapper.
    const offset = root.parentElement.getBoundingClientRect().top
                 - wrapRef.current.getBoundingClientRect().top;
    setGuides({ breaks, pages, offset, scroll: root.scrollTop });
    const sel = q.getSelection();
    if (sel) {
      const b = q.getBounds(sel.index);
      if (b) setCursorPage(pageAt(b.top + root.scrollTop, breaks));
    }
  }, [geometryKey]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!pageGuides) return undefined;
    const q = editor();
    if (!q) return undefined;
    let frame = 0;
    const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(measure); };
    const onScroll = () => setGuides((g) => ({ ...g, scroll: q.root.scrollTop }));
    q.on("editor-change", schedule);
    q.root.addEventListener("scroll", onScroll);
    window.addEventListener("resize", schedule);
    schedule();
    return () => {
      cancelAnimationFrame(frame);
      q.off("editor-change", schedule);
      q.root.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", schedule);
    };
  }, [measure, pageGuides]);   // eslint-disable-line react-hooks/exhaustive-deps

  const showTools = !readOnly && (fields.length > 0 || layoutFields.length > 0 || tables);
  const hasLayout = (key) => layoutFields.some((f) => f.key === key);

  return (
    <div className="space-y-2">
      {pageGuides && <style>{PAGE_EDITOR_CSS}</style>}
      {withSizes && <style>{FONT_SIZE_CSS}</style>}
      {tables && <style>{COL_WIDTH_CSS}</style>}
      {showTools && (
        <div className="flex flex-wrap items-center gap-2">
          {fields.length > 0 && (
            <>
              <Braces className="h-3.5 w-3.5 text-gray-400" />
              <Select
                size="small"
                className={compact ? "w-52" : "w-64"}
                placeholder="Insert a field at the cursor…"
                value={null}
                showSearch
                optionFilterProp="label"
                onChange={insert}
                options={fields.map((f) => ({ value: f.key, label: `${f.label}  ·  {{${f.key}}}` }))}
                getPopupContainer={popupContainer}
              />
            </>
          )}
          {tables && (
            <Dropdown menu={{ items: TABLE_ACTIONS, onClick: tableAction }} trigger={["click"]}
                      getPopupContainer={popupContainer}>
              <button type="button"
                      className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
                <Table2 className="h-3.5 w-3.5" /> Table
              </button>
            </Dropdown>
          )}
          {hasLayout("page_break") && (
            <button type="button" onClick={() => insertBlock("page_break")}
                    title="Everything after this starts on a new page"
                    className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
              <FileStack className="h-3.5 w-3.5" /> Page break
            </button>
          )}
          {hasLayout("signature_block") && (
            <button type="button" onClick={() => insertBlock("signature_block")}
                    title="Both parties sign here — e.g. at the end of each annexure"
                    className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
              <PenLine className="h-3.5 w-3.5" /> Signature block
            </button>
          )}
          {pageGuides && (
            <span className="ml-auto rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300"
                  title="Estimated from the text; Preview PDF shows the exact pages">
              Cursor on page {cursorPage} of ~{guides.pages}
            </span>
          )}
        </div>
      )}
      <div ref={wrapRef}
           className={`relative overflow-hidden rounded-md border border-gray-300 bg-white text-gray-900 dark:border-gray-600 [&_.ql-container]:min-h-0 [&_.ql-container]:flex-1 [&_.ql-container]:overflow-y-auto [&_.ql-container]:border-0 [&_.ql-toolbar]:border-0 [&_.ql-toolbar]:border-b ${pageGuides ? "mou-page-editor bg-gray-100 [&_.ql-container]:overflow-x-auto [&_.ql-editor]:bg-white" : ""}`}>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value || ""}
          onChange={onChange}
          onChangeSelection={onSelection}
          modules={modules}
          readOnly={readOnly}
          placeholder={placeholder}
          // Quill 2.0.3's semantic HTML writes every space as &nbsp;, so a
          // paragraph never wraps in the PDF. Documents store the editor's
          // own HTML (plain spaces, flat lists the PDF builder nests);
          // emails ask for `semantic`, since mail clients need real nested
          // lists — the server makes their spaces breakable again.
          useSemanticHTML={semantic}
          style={{ height, display: "flex", flexDirection: "column" }}
        />
        {pageGuides && slots && headerHtml && createPortal(
          <BandPreview html={headerHtml} cm={bands.headerCm} kind="head" />, slots.top)}
        {pageGuides && slots && footerHtml && createPortal(
          <BandPreview html={footerHtml} cm={bands.footerCm} kind="foot" />, slots.bottom)}
        {pageGuides && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 overflow-hidden"
               style={{ top: guides.offset }}>
            {guides.breaks.map((b) => {
              const y = b.y - guides.scroll;
              if (y < 0 || y > height) return null;
              return (
                <div key={`${b.page}-${b.y}`} className="absolute inset-x-0" style={{ top: y }}>
                  <div className="border-t-2 border-dashed border-indigo-300" />
                  <span className="absolute right-2 -top-2.5 rounded bg-indigo-500 px-1.5 text-[10px] font-medium leading-4 text-white">
                    Page {b.page}{b.forced ? " (page break)" : ""}{headerHtml || footerHtml ? " · header & footer repeat" : ""}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}


/** Read-only header/footer as the PDF prints it, at the page's text width.
 *  Page fields show sample values. Its own HTML from the band editor, also
 *  sanitised on the server when saved. */
function BandPreview({ html, cm, kind }) {
  const shown = html
    .replace(/\{\{\s*page_number\s*\}\}/g, "1")
    .replace(/\{\{\s*page_count\s*\}\}/g, "N");
  return (
    <div className="bg-gray-100 py-1" contentEditable={false}>
      <div className={`mou-band mou-band-${kind}${cm ? " is-banner" : ""}`}
           style={cm ? { maxHeight: cmToPx(BANNER_MAX_CM) + 12 } : undefined}>
        <span className="mou-band-tag">{kind === "head" ? "Header · every page" : "Footer · every page"}</span>
        <div dangerouslySetInnerHTML={{ __html: shown }} />
      </div>
    </div>
  );
}
